import { isValidElement } from 'react';

import { v4 as uuid } from 'uuid';

import { NOTIFICATION_TYPES } from '@constants/notifications';
import { intl } from '@helpers/intl';
import {
  CLEAR_ALL_NOTIFICATIONS,
  CLEAR_NOTIFICATION,
  SET_NOTIFICATIONS,
} from '@store/notifications/notifications.const';

/**
 * Sets a list of notifications
 * @param {string} key - State slice.
 * @param {Array<any>} payload - List of notifications.
 * @returns {{payload: string, type: string}}
 */
export const setNotifications = (key, payload) => ({
  type: `${key}_${SET_NOTIFICATIONS}`,
  payload,
});

/**
 * Clears a single notification
 * @param {string} key - State slice.
 * @param {string} payload - Id to be removed.
 * @returns {{payload: string, type: string}}
 */
export const clearNotification = (key, payload) => ({
  type: `${key}_${CLEAR_NOTIFICATION}`,
  payload,
});

/**
 * Clears all notifications.
 * @param {string} key - State slice.
 * @param {null} payload - Not needed here so we stick to default.
 * @returns {{payload: null, type: string}}
 */
export const clearAllNotifications = (key, payload = null) => ({
  type: `${key}_${CLEAR_ALL_NOTIFICATIONS}`,
  payload,
});

/**
 * Create a toast
 * @param {string} type - Notification type.
 * @param {Object} payload - Notification contents.
 * @param {string} [payload.id] - Optional toast ID.
 * @param {Object|string} payload.message - Message content or translation object for Intl.
 * @param {Object} payload.values - Values to interpolate in intl message. Only used for translation objects.
 * @returns {{payload: Array<Object>, type: string}}
 */
export const toast = (type, payload) => {
  const id = payload.id || uuid();
  const message =
    typeof payload.message === 'object' && !isValidElement(payload.message)
      ? intl.formatMessage(payload.message, payload.values)
      : payload.message;

  return {
    type: `appNotifications_${SET_NOTIFICATIONS}`,
    payload: [
      {
        ...payload,
        id,
        message,
        type,
      },
    ],
  };
};

/**
 * Create a success toast
 * @param {Object} payload - Notification contents.
 * @param {string} [payload.id] - Optional toast ID.
 * @param {Object|string} payload.message - Message content or translation object for Intl.
 * @returns {Function} Toast function
 */
export const toastSuccess = payload => toast(NOTIFICATION_TYPES.SUCCESS, payload);

/**
 * Create an error toast
 * @param {Object} payload - Notification contents.
 * @param {string} [payload.id] - Optional toast ID.
 * @param {Object|string} payload.message - Message content or translation object for Intl.
 * @returns {Function} Toast function
 */
export const toastError = payload => toast(NOTIFICATION_TYPES.ERROR, payload);

/**
 * Create a warning toast
 * @param {Object} payload - Notification contents.
 * @param {string} [payload.id] - Optional toast ID.
 * @param {Object|string} payload.message - Message content or translation object for Intl.
 * @returns {Function} Toast function
 */
export const toastWarning = payload => toast(NOTIFICATION_TYPES.WARNING, payload);

/**
 * Create an info toast
 * @param {Object} payload - Notification contents.
 * @param {string} [payload.id] - Optional toast ID.
 * @param {Object|string} payload.message - Message content or translation object for Intl.
 * @returns {Function} Toast function
 */
export const toastInfo = payload => toast(NOTIFICATION_TYPES.INFO, payload);
